import React, { useState } from "react";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../shared/components/Motion";
import "./styles/colorpicker.css";
import CustomThemeForm from "./CustomThemeForm";
import { ToggleSwitch, ButtonComponent } from "shared/components";
import ThemeButtons from "./ThemeButtons";
import ColorProperties from "./ColorProperties.jsx";
import isEqual from "lodash/isEqual"; // Import isEqual function from lodash
import {
  childVariants,
  accordionContainer,
  accordionWrapper,
  accordionItem,
} from "../../shared/utils";
function ColorPicker({
  setActiveThemeInCustomThemes,
  activeThemeInCustomThemes,
  applyCustomTheme,
  storedCustomThemes,
  storedDefaultThemes,
  applyDefaultTheme,
  enableColors,
  colors,
  setColors,
  activeThemeInDefaultThemes,
  setActiveThemeInDefaultThemes,
  setEnableColors,
  expandedSection,
  setExpandedSection,
}) {
  const [showAddNewThemeBtn, setShowAddNewThemeBtn] = useState(true);

  const handleColorChange = (event) => {
    const { name, value } = event.target;
    setColors((prevColors) => ({
      ...prevColors,
      [name]: value,
    }));
  };

  const handleEnableChange = (event) => {
    const isChecked = event.target.checked;
    setEnableColors(isChecked);
    setShowAddNewThemeBtn(true);
    if (isChecked)
      setExpandedSection((prev) => {
        const next = new Set(prev);
        next.add("customThemes");
        return next;
      });

    if (isChecked) {
      if (storedCustomThemes) {
        applyCustomTheme(
          storedCustomThemes[activeThemeInCustomThemes].colors,
          activeThemeInCustomThemes,
        );
      }
    } else {
      applyDefaultTheme(
        storedDefaultThemes[activeThemeInDefaultThemes].colors,
        activeThemeInDefaultThemes,
      );
    }
    localStorage.setItem("enableColors", isChecked);
  };
  const handleDeleteCustomTheme = () => {
    if (storedCustomThemes.length > 2) {
      const updatedThemes = storedCustomThemes.filter(
        (_, i) => i !== activeThemeInCustomThemes,
      );
      localStorage.setItem("storedCustomThemes", JSON.stringify(updatedThemes));
      // If the currently active custom theme is deleted, switch to the first one
      applyCustomTheme(
        updatedThemes[storedCustomThemes.length - 2].colors,
        storedCustomThemes.length - 2,
      );
      setShowAddNewThemeBtn(true);
    } else {
      localStorage.setItem("storedCustomThemes", []);
      applyDefaultTheme(
        storedDefaultThemes[activeThemeInDefaultThemes].colors,
        activeThemeInDefaultThemes,
      );
    }
  };

  const handleSubmit = () => {
    setShowAddNewThemeBtn(true);
    if (enableColors) {
      ColorProperties.forEach((property) => {
        localStorage.setItem(property.name, colors[property.name]);
      });
    }

    const existingDefaultThemeIndex = storedDefaultThemes.findIndex(
      (theme) => isEqual(theme.colors, colors), // Using deep equality check
    );
    let existingCustomThemeIndex = -1;
    if (storedCustomThemes) {
      existingCustomThemeIndex = storedCustomThemes.findIndex(
        (theme) => isEqual(theme.colors, colors), // Using deep equality check
      );
    }

    if (existingDefaultThemeIndex !== -1) {
      applyDefaultTheme(
        storedDefaultThemes[existingDefaultThemeIndex].colors,
        existingDefaultThemeIndex,
      );
    } else if (existingCustomThemeIndex !== -1) {
      applyCustomTheme(
        storedCustomThemes[existingCustomThemeIndex].colors,
        existingCustomThemeIndex,
      );
    } else {
      const newCustomTheme = {
        name: `Custom Theme ${
          storedCustomThemes ? storedCustomThemes.length : 1
        }`,
        colors: { ...colors },
      };
      const updatedThemes = [
        ...(storedCustomThemes || [
          {
            name: "Delete Selected Custom Theme",
            colors: {
              textColor: "",
              backgroundColor: "",
              headerColor: "",
            },
          },
        ]), // Use an empty array if storedCustomThemes is null or undefined
        newCustomTheme,
      ];
      localStorage.setItem("storedCustomThemes", JSON.stringify(updatedThemes));
      setActiveThemeInCustomThemes(updatedThemes.length - 1);
      localStorage.setItem(
        "activeThemeInCustomThemes",
        updatedThemes.length - 1,
      );
      applyCustomTheme(newCustomTheme.colors, updatedThemes.length - 1);
    }
  };

  return (
    <>
      {/** Render default themes section */}
      <motion.div
        className="themes__section active__color-mix"
        variants={childVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <motion.div
          variants={childVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={() =>
            setExpandedSection((prev) => {
              const next = new Set(prev);
              next.has("defaultThemes")
                ? next.delete("defaultThemes")
                : next.add("defaultThemes");
              return next;
            })
          }
          style={{
            display: "flex",
            gap: "0.5rem",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: "0.85rem",
              fontWeight: "600",
            }}
          >
            <i
              className={`bx bx-chevron-${expandedSection.has("defaultThemes") ? "down" : "right"}`}
            ></i>
          </div>
          <div className="themes__model-content">
            <p className="themes__model-title">Default Themes</p>
          </div>
        </motion.div>

        {/** Render default theme buttons */}
        <AnimatePresence mode="wait">
          {expandedSection.has("defaultThemes") && (
            <motion.div
              variants={accordionContainer}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.div
                className="segmented-control"
                variants={accordionWrapper}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <ThemeButtons
                  enableColors={enableColors}
                  storedDefaultThemes={storedDefaultThemes}
                  activeTheme={activeThemeInDefaultThemes}
                  applyTheme={applyDefaultTheme}
                  setActiveTheme={setActiveThemeInDefaultThemes}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/** Render custom themes section or toggle switch based on conditions */}
      <motion.div
        className={`themes__section ${enableColors ? "active__color-mix" : ""}`}
        variants={childVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/** Render toggle switch for custom themes */}
        <motion.div
          variants={childVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <div className="themes__model-content">
            <ToggleSwitch
              id="customThemeCheckbox"
              modelTitle={"Custom Themes"}
              checked={enableColors}
              onChange={handleEnableChange}
              icons={["uil uil-check-circle", "uil uil-times-circle"]}
              containerClass="colorpicker-toggle-container"
              isExpanded={expandedSection.has("customThemes")}
              onToggleExpand={
                enableColors
                  ? () =>
                      setExpandedSection((prev) => {
                        const next = new Set(prev);
                        next.has("customThemes")
                          ? next.delete("customThemes")
                          : next.add("customThemes");
                        return next;
                      })
                  : undefined
              }
            />
          </div>
        </motion.div>

        {/** Render custom themes section if custom themes are enabled */}
        <AnimatePresence mode="wait">
          {enableColors &&
            expandedSection.has("customThemes") &&
            storedCustomThemes && (
              <motion.div
                variants={accordionContainer}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <motion.div
                  variants={accordionWrapper}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <motion.div
                    variants={accordionItem}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <div className="themes__model-content">
                      <p className="themes__model-title">Custom Themes</p>
                    </div>
                  </motion.div>

                  <motion.div
                    className="segmented-control"
                    variants={accordionItem}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{ delay: 0.05 }}
                  >
                    <ThemeButtons
                      enableColors={enableColors}
                      storedDefaultThemes={storedCustomThemes}
                      activeTheme={activeThemeInCustomThemes}
                      applyTheme={applyCustomTheme}
                      setActiveTheme={setActiveThemeInCustomThemes}
                      handleDeleteCustomTheme={handleDeleteCustomTheme}
                    />
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
        </AnimatePresence>

        {/** Render add new theme button*/}
        {enableColors &&
          expandedSection.has("customThemes") &&
          showAddNewThemeBtn && (
            <motion.div
              variants={accordionContainer}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.div
                variants={accordionWrapper}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <ButtonComponent
                  className="AddNewTheme__buttonComponent"
                  buttonTitle="Add New Theme"
                  buttonText="Add New Theme"
                  onClick={() => setShowAddNewThemeBtn(!showAddNewThemeBtn)}
                />
              </motion.div>
            </motion.div>
          )}

        {/** Render theme form if custom themes are enabled */}
        <AnimatePresence mode="wait">
          {!showAddNewThemeBtn &&
            enableColors &&
            expandedSection.has("customThemes") && (
              <motion.div
                variants={accordionContainer}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ overflow: "hidden" }}
              >
                <motion.div
                  variants={accordionWrapper}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.85rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <CustomThemeForm
                    colorProperties={ColorProperties}
                    colors={colors}
                    handleColorChange={handleColorChange}
                    handleSubmit={handleSubmit}
                    handleFormVisibility={() => setShowAddNewThemeBtn(true)}
                  />
                </motion.div>
              </motion.div>
            )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}

export default ColorPicker;
